import axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import { getAuthToken } from "@/core/auth";
import { getBearerTokenOverride } from "@/core/auth/requestToken";

import { SUCCESS_STATUS_CODES } from "./api.config";

/**
 * Custom mutator for the orval-generated client (see orval.config.ts).
 *
 * Mirrors the legacy api.utils.ts request layer so migrated call sites keep
 * identical runtime behavior:
 * - Bearer auth from the current session token; with no session, from the
 *   override a login-free route registered (see core/auth/requestToken) — so
 *   generated functions stay single-argument and this layer knows nothing
 *   about which pages are public or where their credential comes from
 * - `Cache-Control: no-cache` on GETs
 * - array query params serialized as repeated `key=value` pairs (ASP.NET
 *   binding) — applied globally instead of api.utils.ts's per-path allowlist
 * - legacy BaseCommand `{ result }` envelopes unwrapped; the orval input
 *   transformer (scripts/orval-transform-spec.mjs) rewrites those response
 *   schemas to match, so the generated types line up with what callers receive
 */
export const customAxios = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const token = getAuthToken() ?? getBearerTokenOverride();

  // Caller headers first (generated code sets e.g. Content-Type), then the mutator's own: the
  // resolved credential and the no-cache rule are the mutator's to decide, so nothing a caller
  // passes can silently replace them.
  const { data, status } = await axios.request<unknown>({
    ...config,
    headers: {
      ...config.headers,
      ...(config.method?.toLowerCase() === "get" ? { "Cache-Control": "no-cache" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    paramsSerializer: { indexes: null },
    validateStatus: (statusCode) => SUCCESS_STATUS_CODES.has(statusCode),
  });

  // Unwrap condition intentionally matches the transformer's isEnvelopeSchema
  // (`result` + `runInBackground`), not the legacy layer's looser `"result" in
  // data` — a plain model with its own `result` property must pass through
  // un-unwrapped so runtime shape and generated type agree.
  if (data !== null && typeof data === "object" && "result" in data && "runInBackground" in data) {
    return (data as { result: T }).result;
  }
  return data as T;
};

/**
 * Full-response door, for callers that need the response envelope itself rather than its body —
 * in practice the report downloads, which read `Content-Disposition` for the filename.
 *
 * `customAxios` above deliberately returns only `T`, which is what all 1617 generated functions
 * want; this adds the same auth and no-cache handling without that narrowing. It does NOT apply
 * `validateStatus`, so the caller keeps ownership of its own status handling and error message.
 * Everything that has a generated client should use `customAxios`, not this.
 */
export const customAxiosResponse = async <T>(
  config: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  const token = getAuthToken() ?? getBearerTokenOverride();

  // Same precedence as customAxios: caller headers first, then the credential and the no-cache
  // rule, which are this layer's to decide.
  return await axios.request<T>({
    ...config,
    headers: {
      ...config.headers,
      ...(config.method?.toLowerCase() === "get" ? { "Cache-Control": "no-cache" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    paramsSerializer: { indexes: null },
    validateStatus: () => true,
  });
};

export type ErrorType<E> = AxiosError<E>;

export default customAxios;
